import { action, makeObservable, observable } from 'mobx';
import { gameResultStore } from '@pascal/pascal-core/shared/core/store';
import { CARDS, DECK_ACE_CARDS } from '../enums/cardTypes';
import { toPoint } from '../helpers/toCountCardPoint';
import {
  BET_WIN_TYPE,
  BLACK_JACK_POINT,
  DOUBLE_BET_CODE,
  INSURANCE_BET_CODE,
  SPLIT_HAND_TYPE,
} from '../enums/constants';
import { gameStore } from './gameStore';
import { ADDITIONAL_PARAMS_TYPE } from '../enums/actionTypes';
import { soundService, soundTypes } from '../services/SoundService';

class CardsStore {
  playerCards = [];

  dealerCardsInfo = {
    point: null,
    cards: null,
  };

  boxCount = 3;

  animationByBox = {
    0: '',
    1: '',
    2: '',
  };

  dealerAnimationClassName = '';

  isStartCardImitation = false;

  openedCardsCount = null;

  cuttingCardPlace = null;

  selectedCardType = 'calode-1';

  selectedCardBackSideType = 'backside-1';

  isCuttingCardOpen = false;

  isShuffle = false;

  shuffleAnimationType = '';

  constructor() {
    makeObservable(this, {
      isShuffle: observable,
      boxCount: observable.deep,
      openedCardsCount: observable,
      playerCards: observable.deep,
      selectedCardType: observable,
      cuttingCardPlace: observable,
      isCuttingCardOpen: observable,
      dealerAnimationClassName: observable,
      animationByBox: observable.deep,
      shuffleAnimationType: observable,
      dealerCardsInfo: observable.deep,
      isStartCardImitation: observable,
      selectedCardBackSideType: observable,
      resetCards: action.bound,
      setBoxCount: action.bound,
      setIsShuffle: action.bound,
      setPlayerCards: action.bound,
      splitBoxByHand: action.bound,
      createCardsBox: action.bound,
      setDealerCardsInfo: action.bound,
      updateAnimationBox: action.bound,
      setCuttingCardPlace: action.bound,
      setSelectedCardType: action.bound,
      setOpenedCardsCount: action.bound,
      setIsCuttingCardOpen: action.bound,
      updateDealerCardsInfo: action.bound,
      updatePlayerCardsByBox: action.bound,
      setShuffleAnimationType: action.bound,
      handleInitialStateByHand: action.bound,
      updateWinningAmountByBox: action.bound,
      updateSurrenderResultByBox: action.bound,
      updateIsStartCardImitation: action.bound,
      updateSelectedDecisionByBox: action.bound,
      setSelectedCardBackSideType: action.bound,
      setDealerAnimationClassName: action.bound,
    });
  }

  updateSurrenderResultByBox(boxIndex) {
    this.playerCards = this.playerCards.map((item) =>
      item.boxIndex === boxIndex
        ? {
            ...item,
            hands: { main: { ...item.hands.main, isSurrender: true } },
          }
        : { ...item }
    );
  }

  setDealerAnimationClassName(arg) {
    this.dealerAnimationClassName = arg;
  }

  setCuttingCardPlace(arg) {
    this.cuttingCardPlace = arg;
  }

  setOpenedCardsCount(count) {
    this.openedCardsCount = count;
  }

  setShuffleAnimationType(type) {
    this.shuffleAnimationType = type;
  }

  setIsShuffle(bool) {
    this.isShuffle = bool;
  }

  setIsCuttingCardOpen(arg) {
    this.isCuttingCardOpen = arg;
  }

  setSelectedCardBackSideType(type) {
    this.selectedCardBackSideType = type;
  }

  setSelectedCardType(type) {
    this.selectedCardType = type;
  }

  updateAnimationBox(box, className) {
    this.animationByBox[box] = className;
  }

  updateIsStartCardImitation(bool) {
    this.isStartCardImitation = bool;
  }

  setPlayerCards(cardsModel) {
    this.playerCards = cardsModel;
  }

  setBoxCount(count) {
    this.boxCount = count;
    this.createCardsBox(count);
    this.createAnimationBoxModel(count);
  }

  createAnimationBoxModel(count) {
    new Array(count).fill(null).forEach((item, index) => {
      this.animationByBox[index] = '';
    });
  }

  createCardsBox(count) {
    const playerCardsModel = new Array(count).fill(null).map((item, index) => ({
      boxIndex: index,
      amount: 0,
      hands: null,
    }));
    this.playerCards = playerCardsModel;
  }

  splitBoxByHand({ boxIndex }) {
    const playerBoxIndex = this.playerCards.findIndex((item) => item.boxIndex === boxIndex);
    let playerBox = this.playerCards[playerBoxIndex];
    playerBox = {
      ...playerBox,
      hands: {
        main: {
          cards: [playerBox.hands.main.cards[0]],
          point: toPoint(playerBox.hands.main.cards[0].card),
        },
        split: {
          cards: [playerBox.hands.main.cards[1]],
          point: toPoint(playerBox.hands.main.cards[1].card),
        },
      },
    };
    this.playerCards.splice(playerBoxIndex, 1, playerBox);
  }

  createPlayerCardModel({
    playerBox,
    openedCard: card,
    point,
    isDouble,
    hasBlackJack,
    secondPoint,
  }) {
    const { hands } = playerBox;
    const { handType } = card;
    if (!hands) {
      return { main: { cards: [card], point, isDouble } };
    }
    if (handType === SPLIT_HAND_TYPE) {
      const isOpenedSplitCard = hands.split.cards.some((item) => item.index === card.index);
      if (!isOpenedSplitCard) {
        return {
          ...hands,
          split: { cards: [...hands.split.cards, card], point, isDouble, secondPoint },
        };
      }
      return { ...hands };
    }
    const isOpenedMainCard = hands.main.cards.some((item) => item.index === card.index);
    if (!isOpenedMainCard) {
      return {
        ...hands,
        main: { cards: [...hands.main.cards, card], point, isDouble, hasBlackJack, secondPoint },
      };
    }
    return { ...hands };
  }

  updateCards({
    boxIndex,
    totalPoint: point,
    openedCard,
    isDouble = false,
    hasBlackJack = false,
    secondPoint,
  }) {
    soundService.play(soundTypes.Card);
    const results = [...gameResultStore.gameResults];
    const cardIndex = results.findIndex((item) => item.index === openedCard.index);

    if (cardIndex > -1 && cardIndex < results.length) {
      results.splice(cardIndex, 1, openedCard);
      gameResultStore.setGameResults(results);
    }
    if (openedCard.card === CARDS.CUTTING_CARD) {
      this.setIsCuttingCardOpen(true);
      return;
    }
    if (boxIndex !== null) {
      gameStore.setIsSplitCardAnimationByBox(openedCard.handType);
      this.updatePlayerCardsByBox({
        boxIndex,
        openedCard,
        point,
        isDouble,
        hasBlackJack,
        secondPoint,
      });
    } else {
      gameStore.setActiveBox(null);
      gameStore.setActiveHandByBox(null);
      this.updateDealerCardsInfo({ openedCard, point, hasBlackJack, secondPoint });
    }
  }

  updateSelectedDecisionByBox({ boxIndex, decision, hand, isRemoveSecondPoint }) {
    const selectedBoxIndex = this.playerCards.findIndex((item) => item.boxIndex === boxIndex);
    const playerBox = this.playerCards[selectedBoxIndex];
    if (hand === SPLIT_HAND_TYPE) {
      playerBox.hands.split.decision = decision;
    } else {
      playerBox.hands.main.decision = decision;
      if (isRemoveSecondPoint) {
        playerBox.hands.main.secondPoint = 0;
      }
    }
    this.playerCards.splice(selectedBoxIndex, 1, playerBox);
  }

  updatePlayerCardsByBox({ boxIndex, openedCard, point, isDouble, hasBlackJack, secondPoint }) {
    const playerBoxIndex = this.playerCards.findIndex((item) => item.boxIndex === boxIndex);
    let playerBox = this.playerCards[playerBoxIndex];
    playerBox = {
      ...playerBox,
      hands: {
        ...this.createPlayerCardModel({
          playerBox,
          openedCard,
          point,
          isDouble,
          hasBlackJack,
          secondPoint,
        }),
      },
    };
    this.playerCards.splice(playerBoxIndex, 1, playerBox);
  }

  setDealerCardsInfo(dealerCards, dealerCardsPoint) {
    this.dealerCardsInfo = { cards: dealerCards, point: dealerCardsPoint };
  }

  updateDealerCardsInfo({ openedCard, point, hasBlackJack, secondPoint }) {
    const { cards } = this.dealerCardsInfo;
    const darkCardIndex = cards?.findIndex((card) => card.index === openedCard.index);
    if (darkCardIndex > -1 && cards?.length > darkCardIndex) {
      if (
        point === BLACK_JACK_POINT &&
        !DECK_ACE_CARDS.some((cardNumb) => cardNumb === openedCard.card)
      ) {
        gameStore.setActiveBox(null);
      }
      cards.splice(darkCardIndex, 1, openedCard);
      this.dealerCardsInfo = { point, cards: [...cards], secondPoint };
    } else {
      const updatedDealerCards = cards ? [...cards, openedCard] : [openedCard];
      this.dealerCardsInfo = { point, cards: updatedDealerCards, hasBlackJack, secondPoint };
    }
  }

  handleInitialStateByHand(bets) {
    const keys = new Array(this.boxCount).fill(null);
    const cardsModel = keys.map((el, index) => {
      const cardByBet = bets[index]?.boxBets;
      let model;
      if (cardByBet) {
        const mainHand = cardByBet.find((item) => item.mainBet);
        const splitHand = cardByBet.find((item) => item.splitBet);
        const doubleBet = cardByBet.find((item) => item.doubleBet);
        const { mainBetCards, totalPoint, hasBlackJack, hasMainBetSurrender, lastAction } =
          mainHand;
        model = {
          boxIndex: +index,
          hands: {
            main: {
              cards: [...mainBetCards],
              totalPoint,
              point: totalPoint,
              isSurrender: hasMainBetSurrender,
              hasBlackJack,
              decision: lastAction,
              isDouble: Boolean(doubleBet),
            },
          },
        };
        if (splitHand) {
          const { totalPoint: point, splitBetCards, lastAction: decision } = splitHand;
          model.hands.split = { point, cards: splitBetCards, decision };
        }
      } else {
        model = {
          boxIndex: index,
          hands: null,
        };
      }
      return model;
    });
    this.playerCards = cardsModel;
  }

  updateWinningAmountByBox(bets) {
    const withoutDoubleBet = bets.filter(
      (bet) =>
        bet.additionalParams.type !== DOUBLE_BET_CODE &&
        bet.additionalParams.type !== INSURANCE_BET_CODE
    );
    withoutDoubleBet.forEach(
      ({ additionalParams: { boxIndex, markets, type, winType }, ...rest }) => {
        this.playerCards = this.playerCards.map((box) => {
          const handName = type === ADDITIONAL_PARAMS_TYPE.MAIN_BET ? 'main' : 'split';
          const mainBet = markets?.find((item) => item.type === ADDITIONAL_PARAMS_TYPE.MAIN_BET);
          return box.boxIndex === boxIndex
            ? {
                ...box,
                hands: {
                  ...box.hands,
                  [handName]: {
                    ...box.hands[handName],
                    winAmount:
                      type === ADDITIONAL_PARAMS_TYPE.MAIN_BET
                        ? mainBet?.winAmount
                        : rest.winAmount,
                    isPush: winType === BET_WIN_TYPE.Push,
                    isShowBjAnim: mainBet?.winType === BET_WIN_TYPE.BlackJack,
                  },
                },
              }
            : { ...box };
        });
      }
    );
  }

  resetCards() {
    this.playerCards = [];
    this.dealerCardsInfo = { cards: null, point: null };
  }
}

export const cardsStore = new CardsStore();
